import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
    WifiOff, RefreshCw, Loader2, Search, X, MapPin, Clock,
    ChevronRight, CheckCircle2, Cpu, AlertTriangle,
    ArrowUpDown, SearchX, CalendarClock
} from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5022';


// ✅ Единая «общая» иконка прибора учёта для всех типов.
// Типы различаются только цветом (синий / голубой / янтарный).
// Альтернативы при желании: Activity, Thermometer, Meter.
const TYPE_CONFIG = {
    'Аксиома-03': { icon: Cpu, color: 'text-blue-400', bg: 'bg-blue-500/15' },
    'Аксиома-01': { icon: Cpu, color: 'text-cyan-400', bg: 'bg-cyan-500/15' },
    'Карат-307': { icon: Cpu, color: 'text-amber-400', bg: 'bg-amber-500/15' }
};
const DEVICE_TYPES = Object.keys(TYPE_CONFIG);

// Серьёзность ситуации по дням без связи
const getSeverity = (days) => {
    if (days >= 7) return {
        label: 'Критично',
        badge: 'bg-red-500/20 text-red-400 border border-red-500/40',
        dot: 'bg-red-500', border: 'border-l-red-500', text: 'text-red-400', pulse: true
    };
    if (days >= 3) return {
        label: 'Внимание',
        badge: 'bg-orange-500/20 text-orange-400 border border-orange-500/40',
        dot: 'bg-orange-500', border: 'border-l-orange-500', text: 'text-orange-400', pulse: false
    };
    return {
        label: 'Недавно',
        badge: 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/40',
        dot: 'bg-yellow-500', border: 'border-l-yellow-500', text: 'text-yellow-400', pulse: false
    };
};

const plural = (n, one, few, many) => {
    const abs = Math.abs(n) % 100;
    const d = abs % 10;
    if (abs > 10 && abs < 20) return many;
    if (d === 1) return one;
    if (d >= 2 && d <= 4) return few;
    return many;
};
const pluralDays = (n) => `${n} ${plural(n, 'день', 'дня', 'дней')}`;

const formatDateTime = (iso) => {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return '—';
    return d.toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const formatAddress = (d) =>
    [...[d.city, d.street].filter(Boolean), `д. ${d.houseNumber || '?'}`].join(', ');

const LostDevicesTab = ({ onCountChange }) => {
    const navigate = useNavigate();
    const [lostDevices, setLostDevices] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchText, setSearchText] = useState('');
    const [typeFilter, setTypeFilter] = useState('all');
    const [sortDesc, setSortDesc] = useState(true);
    const [lastUpdated, setLastUpdated] = useState(null);

    useEffect(() => {
        loadLostDevices();
    }, []);

    // ✅ Передаём количество потерянных устройств родителю (для бейджа на вкладке)
    useEffect(() => {
        onCountChange?.(lostDevices.length);
    }, [lostDevices, onCountChange]);

    const loadLostDevices = async () => {
        setIsLoading(true);
        setError(null);
        try {
            const token = localStorage.getItem('token');

            const response = await axios.get(`${API_URL}/api/ns/lost-devices`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            setLostDevices(response.data || []);
            setLastUpdated(new Date());
        } catch (err) {
            console.error('Ошибка загрузки потерянных устройств:', err);
            setError('Не удалось загрузить список устройств, не выходящих на связь');
        } finally {
            setIsLoading(false);
        }
    };

    const stats = useMemo(() => {
        const byType = {};
        let critical = 0;
        let maxDays = 0;
        lostDevices.forEach(d => {
            byType[d.deviceType] = (byType[d.deviceType] || 0) + 1;
            if (d.daysOffline >= 7) critical++;
            if (d.daysOffline > maxDays) maxDays = d.daysOffline;
        });
        return { total: lostDevices.length, byType, critical, maxDays };
    }, [lostDevices]);

    const filteredDevices = useMemo(() => {
        const q = searchText.toLowerCase().trim();
        let list = lostDevices.filter(d => typeFilter === 'all' || d.deviceType === typeFilter);
        if (q) {
            list = list.filter(d =>
                (d.name || '').toLowerCase().includes(q) ||
                (d.city || '').toLowerCase().includes(q) ||
                (d.street || '').toLowerCase().includes(q) ||
                (d.houseNumber || '').toLowerCase().includes(q)
            );
        }
        return [...list].sort((a, b) =>
            sortDesc ? b.daysOffline - a.daysOffline : a.daysOffline - b.daysOffline
        );
    }, [lostDevices, searchText, typeFilter, sortDesc]);

    // ✅ Переход в «Объект учета» — тот же механизм, что и с карты:
    // DevicesPage при монтировании читает selectedDeviceFromMap из localStorage
    const openInDevicesPage = (device) => {
        localStorage.setItem('selectedDeviceFromMap', JSON.stringify(device));
        navigate('/devices');
    };

    return (
        <div className="space-y-4 sm:space-y-6">
            {/* ===== Панель действий вкладки ===== */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <p className="text-slate-400 text-xs sm:text-sm">
                    Приборы на обслуживании, не выходившие на связь с сервером сегодня
                </p>
                <div className="flex items-center gap-3">
                    {lastUpdated && (
                        <span className="text-slate-500 text-xs hidden sm:inline tabular-nums">
                            Обновлено: {lastUpdated.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                        </span>
                    )}
                    <button
                        onClick={loadLostDevices}
                        disabled={isLoading}
                        className="px-4 py-2 bg-blue-600 hover:bg-blue-500 disabled:opacity-60 text-white rounded-lg text-sm font-medium transition flex items-center gap-2"
                    >
                        <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} /> Обновить
                    </button>
                </div>
            </div>

            {/* ===== Загрузка ===== */}
            {isLoading && (
                <div className="bg-slate-800 rounded-xl border border-slate-700 py-16 flex flex-col items-center">
                    <Loader2 className="animate-spin h-8 w-8 text-blue-500 mb-3" />
                    <div className="text-slate-400 text-sm">Проверка связи с устройствами...</div>
                </div>
            )}

            {/* ===== Ошибка ===== */}
            {!isLoading && error && (
                <div className="bg-red-500/20 border border-red-500/50 text-red-200 px-4 py-3 rounded-lg text-sm flex items-center justify-between gap-3">
                    <span className="flex items-center gap-2">
                        <AlertTriangle className="w-4 h-4 flex-shrink-0" /> {error}
                    </span>
                    <button
                        onClick={loadLostDevices}
                        className="px-3 py-1.5 bg-red-500/20 hover:bg-red-500/40 rounded-lg text-xs font-medium transition whitespace-nowrap"
                    >
                        Повторить
                    </button>
                </div>
            )}

            {/* ===== Все устройства на связи ===== */}
            {!isLoading && !error && lostDevices.length === 0 && (
                <div className="bg-slate-800 rounded-xl border border-slate-700 py-16 flex flex-col items-center text-center px-4">
                    <div className="relative mb-5">
                        <div className="w-16 h-16 rounded-full bg-green-500/15 border border-green-500/30 flex items-center justify-center">
                            <CheckCircle2 className="w-8 h-8 text-green-400" />
                        </div>
                        <span className="absolute inset-0 rounded-full border border-green-500/30 animate-ping" style={{ animationDuration: '2.5s' }} />
                    </div>
                    <h3 className="text-lg font-bold text-white mb-1">Все приборы на связи</h3>
                    <p className="text-slate-400 text-sm max-w-md">
                        Все приборы на обслуживании соединялись с сервером сегодня — потеря связи отсутствует.
                    </p>
                </div>
            )}

            {/* ===== Есть потерянные устройства ===== */}
            {!isLoading && !error && lostDevices.length > 0 && (
                <>
                    {/* Сводка + фильтр по типам */}
                    <div className="bg-slate-800 rounded-xl border border-slate-700 p-4 sm:p-5">
                        <div className="flex flex-wrap items-center gap-x-6 gap-y-4 sm:gap-x-8">
                            <div className="flex items-center gap-4">
                                <div className="w-12 h-12 rounded-lg bg-red-500/15 border border-red-500/30 flex items-center justify-center flex-shrink-0">
                                    <WifiOff className="w-6 h-6 text-red-400" />
                                </div>
                                <div>
                                    <div className="text-3xl font-bold text-white leading-none tabular-nums">{stats.total}</div>
                                    <div className="text-slate-400 text-xs mt-1.5">не выходят на связь</div>
                                </div>
                            </div>

                            <div className="hidden sm:block h-10 w-px bg-slate-700" />

                            <div className="flex flex-wrap items-center gap-2">
                                {DEVICE_TYPES.map(type => {
                                    const cfg = TYPE_CONFIG[type];
                                    const Icon = cfg.icon;
                                    const count = stats.byType[type] || 0;
                                    const active = typeFilter === type;
                                    return (
                                        <button
                                            key={type}
                                            onClick={() => setTypeFilter(active ? 'all' : type)}
                                            disabled={count === 0}
                                            className={`px-3 py-2 rounded-lg border text-xs font-medium transition flex items-center gap-2 ${active
                                                    ? 'bg-blue-600/30 border-blue-500 text-white'
                                                    : count === 0
                                                        ? 'bg-slate-700/30 border-slate-700 text-slate-600 cursor-default'
                                                        : 'bg-slate-700/50 border-slate-600 text-slate-300 hover:border-slate-500 hover:text-white'
                                                }`}
                                        >
                                            <Icon className={`w-3.5 h-3.5 ${count > 0 ? cfg.color : ''}`} />
                                            {type}
                                            <span className="tabular-nums font-bold">{count}</span>
                                        </button>
                                    );
                                })}
                            </div>

                            <div className="hidden sm:block h-10 w-px bg-slate-700" />

                            <div className="flex items-center gap-6">
                                <div>
                                    <div className={`text-xl font-bold leading-none tabular-nums ${stats.critical > 0 ? 'text-red-400' : 'text-slate-300'}`}>
                                        {stats.critical}
                                    </div>
                                    <div className="text-slate-400 text-xs mt-1.5">критично (7+ дней)</div>
                                </div>
                                <div className="hidden md:block">
                                    <div className="text-xl font-bold leading-none text-slate-200 tabular-nums flex items-center gap-1.5">
                                        <CalendarClock className="w-4 h-4 text-slate-500" />
                                        {stats.maxDays > 0 ? pluralDays(stats.maxDays) : '—'}
                                    </div>
                                    <div className="text-slate-400 text-xs mt-1.5">максимум без связи</div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Поиск */}
                    <div className="flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
                        <div className="relative sm:max-w-xs sm:flex-1">
                            <input
                                type="text"
                                placeholder="Поиск по имени или адресу..."
                                value={searchText}
                                onChange={(e) => setSearchText(e.target.value)}
                                className="w-full px-3 py-2 pl-9 pr-9 bg-slate-800 border border-slate-600 rounded-lg text-white text-sm placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                            <Search className="w-4 h-4 text-slate-500 absolute left-3 top-3" />
                            {searchText && (
                                <button onClick={() => setSearchText('')} className="absolute right-3 top-2.5 text-slate-500 hover:text-white transition" title="Очистить">
                                    <X className="w-4 h-4" />
                                </button>
                            )}
                        </div>
                        <div className="text-slate-500 text-xs sm:text-sm">
                            Показано: <span className="text-slate-300 font-semibold tabular-nums">{filteredDevices.length}</span> из {lostDevices.length}
                        </div>
                    </div>

                    {filteredDevices.length === 0 ? (
                        <div className="bg-slate-800 rounded-xl border border-slate-700 py-12 flex flex-col items-center text-center px-4">
                            <SearchX className="w-10 h-10 text-slate-600 mb-3" />
                            <h3 className="text-white font-bold mb-1">Ничего не найдено</h3>
                            <p className="text-slate-400 text-sm">Попробуйте изменить поисковый запрос или фильтр</p>
                            <button
                                onClick={() => { setSearchText(''); setTypeFilter('all'); }}
                                className="mt-4 px-4 py-2 bg-slate-700 hover:bg-slate-600 text-slate-200 rounded-lg text-sm font-medium transition"
                            >
                                Сбросить фильтры
                            </button>
                        </div>
                    ) : (
                        <>
                            {/* ===== Мобильная версия — карточки ===== */}
                            <div className="sm:hidden space-y-3">
                                {filteredDevices.map(d => {
                                    const sev = getSeverity(d.daysOffline);
                                    const cfg = TYPE_CONFIG[d.deviceType] || TYPE_CONFIG['Аксиома-01'];
                                    const Icon = cfg.icon;
                                    return (
                                        <div key={`${d.deviceType}-${d.name}`} className={`bg-slate-800 rounded-xl border border-slate-700 border-l-4 ${sev.border} p-4 space-y-3`}>
                                            <div className="flex items-start justify-between gap-2">
                                                <div className="flex items-center gap-2.5 min-w-0">
                                                    <div className={`w-9 h-9 rounded-lg ${cfg.bg} flex items-center justify-center flex-shrink-0`}>
                                                        <Icon className={`w-4 h-4 ${cfg.color}`} />
                                                    </div>
                                                    <div className="min-w-0">
                                                        <div className="text-white font-medium text-sm truncate">{d.name}</div>
                                                        <div className="text-slate-500 text-xs">{d.deviceType}</div>
                                                    </div>
                                                </div>
                                                <span className={`px-2 py-1 rounded text-xs font-semibold flex-shrink-0 ${sev.badge}`}>
                                                    {pluralDays(d.daysOffline)}
                                                </span>
                                            </div>
                                            <div className="flex items-start gap-2 text-slate-300 text-sm">
                                                <MapPin className="w-4 h-4 text-slate-500 flex-shrink-0 mt-0.5" />
                                                <span>{formatAddress(d)}</span>
                                            </div>
                                            <div className="flex items-center gap-2 text-slate-400 text-xs">
                                                <Clock className="w-3.5 h-3.5 text-slate-500 flex-shrink-0" />
                                                Последний выход: <span className="tabular-nums">{formatDateTime(d.lastConnect)}</span>
                                            </div>
                                            <button
                                                onClick={() => openInDevicesPage(d)}
                                                className="w-full px-3 py-2 bg-blue-600/20 hover:bg-blue-600 text-blue-400 hover:text-white rounded-lg text-sm font-medium transition flex items-center justify-center gap-1.5"
                                            >
                                                Открыть в «Объект учета» <ChevronRight className="w-4 h-4" />
                                            </button>
                                        </div>
                                    );
                                })}
                            </div>

                            {/* ===== Десктопная версия — таблица ===== */}
                            <div className="hidden sm:block bg-slate-800 rounded-xl border border-slate-700 overflow-hidden">
                                <div className="overflow-x-auto">
                                    <table className="w-full">
                                        <thead className="bg-slate-700/50">
                                            <tr>
                                                <th className="text-left text-slate-300 text-sm font-semibold px-4 py-3 w-28">Без связи</th>
                                                <th className="text-left text-slate-300 text-sm font-semibold px-4 py-3">Устройство</th>
                                                <th className="text-left text-slate-300 text-sm font-semibold px-4 py-3">Адрес</th>
                                                <th className="text-left text-slate-300 text-sm font-semibold px-4 py-3">
                                                    <button onClick={() => setSortDesc(p => !p)} className="flex items-center gap-1.5 hover:text-white transition" title="Изменить сортировку">
                                                        Последний выход
                                                        <ArrowUpDown className={`w-3.5 h-3.5 transition ${sortDesc ? 'text-blue-400' : 'text-slate-500 rotate-180'}`} />
                                                    </button>
                                                </th>
                                                <th className="px-4 py-3 w-40"></th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {filteredDevices.map(d => {
                                                const sev = getSeverity(d.daysOffline);
                                                const cfg = TYPE_CONFIG[d.deviceType] || TYPE_CONFIG['Аксиома-01'];
                                                const Icon = cfg.icon;
                                                return (
                                                    <tr
                                                        key={`${d.deviceType}-${d.name}`}
                                                        onClick={() => openInDevicesPage(d)}
                                                        className={`border-t border-slate-700 border-l-4 ${sev.border} hover:bg-slate-700/30 transition cursor-pointer group`}
                                                    >
                                                        <td className="px-4 py-3">
                                                            <div className="flex items-center gap-2.5">
                                                                <span
                                                                    className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${sev.dot} ${sev.pulse ? 'animate-pulse' : ''}`}
                                                                    title={sev.label}
                                                                />
                                                                <div>
                                                                    <div className={`font-bold tabular-nums leading-none ${sev.text}`}>{d.daysOffline}</div>
                                                                    <div className="text-slate-500 text-[11px] mt-1">{plural(d.daysOffline, 'день', 'дня', 'дней')}</div>
                                                                </div>
                                                            </div>
                                                        </td>
                                                        <td className="px-4 py-3">
                                                            <div className="flex items-center gap-3 min-w-0">
                                                                <div className={`w-9 h-9 rounded-lg ${cfg.bg} flex items-center justify-center flex-shrink-0`}>
                                                                    <Icon className={`w-4 h-4 ${cfg.color}`} />
                                                                </div>
                                                                <div className="min-w-0">
                                                                    <div className="text-white text-sm font-medium truncate">{d.name}</div>
                                                                    <div className="text-slate-500 text-xs">{d.deviceType}</div>
                                                                    {d.statusDescription && (
                                                                        <div className="text-slate-500 text-xs truncate italic max-w-[240px]">{d.statusDescription}</div>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        </td>
                                                        <td className="px-4 py-3">
                                                            <div className="flex items-start gap-2 text-slate-300 text-sm">
                                                                <MapPin className="w-4 h-4 text-slate-500 flex-shrink-0 mt-0.5" />
                                                                <span>{formatAddress(d)}</span>
                                                            </div>
                                                        </td>
                                                        <td className="px-4 py-3">
                                                            <div className="text-slate-300 text-sm tabular-nums flex items-center gap-2">
                                                                <Clock className="w-4 h-4 text-slate-500 flex-shrink-0" />
                                                                {formatDateTime(d.lastConnect)}
                                                            </div>
                                                            <div className={`text-xs mt-1 ${sev.text}`}>{pluralDays(d.daysOffline)} назад</div>
                                                        </td>
                                                        <td className="px-4 py-3 text-right">
                                                            <button
                                                                onClick={(e) => { e.stopPropagation(); openInDevicesPage(d); }}
                                                                className="px-3 py-1.5 bg-blue-600/20 hover:bg-blue-600 text-blue-400 hover:text-white rounded-lg text-xs font-medium transition inline-flex items-center gap-1 group-hover:translate-x-0.5"
                                                            >
                                                                Объект учета <ChevronRight className="w-3.5 h-3.5" />
                                                            </button>
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </>
                    )}
                </>
            )}
        </div>
    );
};

export default LostDevicesTab;